import axios from "axios";

// Cliente por defecto de una venta de mostrador. Vive acá y no en el data() de un
// componente porque Pos.vue destruye tablaCarrito.vue con `v-if="!ModuloPagoState"` al
// abrir el módulo de pago: al volver, Vue lo recreaba y el cliente elegido caía a este
// default sin que el cajero lo notara. Con Factura A eso significa facturar a Consumidor
// Final. El store sobrevive al v-if; el data() de un componente, no.
const CLIENTE_POR_DEFECTO = Object.freeze({
  id_cliente: 1,
  nombre_completo: 'Consumidor Final'
});

const CLAVE_CLIENTE = 'clientePOS';

// Modos de escaneo, de UN SOLO DISPARO: el lector escucha siempre y un botón define qué
// hace el PRÓXIMO escaneo; después vuelve solo a 'normal'. En un mostrador, un modo que
// queda prendido termina borrando productos que el cajero creía estar cargando — por eso
// nunca es pegajoso. Set cerrado: cualquier otro valor cae en 'normal'.
const MODO_ESCANEO_NORMAL = 'normal';
const MODO_ESCANEO_ELIMINAR = 'eliminar';
const MODO_ESCANEO_SUMAR = 'sumar';
const MODOS_ESCANEO_ARMABLES = [MODO_ESCANEO_ELIMINAR, MODO_ESCANEO_SUMAR];

// Se conserva la MISMA clave de localStorage que ya usaban tablaCarrito.vue,
// pagoCuentaCorriente.vue y pauseResumeSaleMixin.js: el store pasa a ser el dueño del
// dato, pero los lectores existentes siguen encontrándolo donde siempre. Migrar el dato
// y los lectores en el mismo paso habría tocado media pantalla de POS de una sola vez.
const leerClienteGuardado = () => {
  try {
    return JSON.parse(localStorage.getItem(CLAVE_CLIENTE)) || { ...CLIENTE_POR_DEFECTO };
  } catch (error) {
    // Un JSON corrupto no puede dejar el POS sin cliente: se cae al default.
    return { ...CLIENTE_POR_DEFECTO };
  }
};

const state = () => ({
  carrito: JSON.parse(localStorage.getItem('carrito')) || [],
  activarModuloPago: false,
  mediosPagos: [],
  productosPOS: [],
  porcentaje: 0,
  totalCarritoManual: null,
  datosResumen: null,
  cliente: leerClienteGuardado(),
  modoEscaneo: MODO_ESCANEO_NORMAL,
  notasCreditoAplicadas: [] // NC aplicadas como medio de pago
});

// Función helper para calcular el total de un item
const calcularTotalItem = (item) => {
  // Si es producto pesable (fiambre = 1), usar cantidad (peso)
  if (item.fiambre === 1) {
    return item.precio_unitario * (item.cantidad || 0);
  }
  
  // Si es producto de bulto cerrado que requiere dialog (tiene cantidad definida)
  // debe calcularse por peso aunque no sea fiambre
  if (item.requiere_dialog_unidades && item.cantidad !== undefined && item.cantidad !== null) {
    return item.precio_unitario * item.cantidad;
  }
  
  // Para productos normales con bulto_cerrado > 1 (presentaciones)
  // Calcular: precio × unidades × bulto_cerrado
  // Ejemplo: 1 cartón × 30 huevos × $228 = $6,840
  if (item.bulto_cerrado && item.bulto_cerrado > 1) {
    return item.precio_unitario * item.unidades * item.bulto_cerrado;
  }
  
  // Para productos unitarios normales, usar solo unidades
  return item.precio_unitario * item.unidades;
};

const getters = {
  carritoState(state) {
    return state.carrito;
  },
  productosPOSState(state) {
    return state.productosPOS;
  },
  mediosPagosState(state) {
    return state.mediosPagos;
  },
  ModuloPagoState(state) {
    return state.activarModuloPago;
  },
  porcentajeDescuento(state) {
    return state.porcentaje;
  },
  notasCreditoAplicadasState(state) {
    return state.notasCreditoAplicadas;
  },
  
  totalNCState(state) {
    // Calcular el subtotal directamente sin usar getters para evitar dependencia circular
    const subtotalCarrito = state.carrito.reduce((total, item) => {
      return total + calcularTotalItem(item);
    }, 0);
    
    let saldoRestante = subtotalCarrito;
    
    return state.notasCreditoAplicadas.reduce((total, nc) => {
      const montoAplicado = saldoRestante > 0
        ? Math.min(nc.saldoDisponible || 0, saldoRestante)
        : 0;
      
      saldoRestante -= montoAplicado;
      return total + montoAplicado;
    }, 0);
  },
  
  totalPagadoState(state) {
    // Solo suma medios de pago (NC ahora son descuentos, no pagos)
    return state.mediosPagos.reduce((total, item) => {
      const pago = parseFloat(item.pago || 0);
      return total + pago;
    }, 0);
  },

  saldoRestanteState(state, getters) {
    const totalCarrito = getters.totalCarrito || 0;
    const totalPagado = getters.totalPagadoState || 0;
    return Number((totalCarrito - totalPagado).toFixed(2));
  },

  totalCarrito(state) {
    // Si hay un valor establecido manualmente, usarlo
    if (state.totalCarritoManual !== null) {
      return state.totalCarritoManual;
    }
    
    // Calcular subtotal
    const subtotal = state.carrito.reduce((total, item) => {
      return total + calcularTotalItem(item);
    }, 0);
    
    const descuento = subtotal * (state.porcentaje / 100);
    
    // Calcular descuento NC directamente sin usar getters
    let saldoRestante = subtotal;
    const descuentoNC = state.notasCreditoAplicadas.reduce((total, nc) => {
      const montoAplicado = saldoRestante > 0
        ? Math.min(nc.saldoDisponible || 0, saldoRestante)
        : 0;
      
      saldoRestante -= montoAplicado;
      return total + montoAplicado;
    }, 0);
    
    return Number((subtotal - descuento - descuentoNC).toFixed(2));
  },
  subtotalCarrito(state) {
    return Number(state.carrito.reduce((total, item) => {
      return total + calcularTotalItem(item);
    }, 0).toFixed(2));
  },
  datosResumenState(state) {
    return state.datosResumen;
  },
  clienteState(state) {
    return state.cliente;
  },
  modoEscaneoState(state) {
    return state.modoEscaneo;
  }
};
const mutations = {
  // Persiste SIEMPRE, no solo cuando el cliente tiene cuenta corriente. Ese `if` era la
  // segunda mitad del bug: un cliente sin cuenta corriente nunca se guardaba y se perdía
  // al recrearse el componente.
  setCliente(state, cliente) {
    state.cliente = cliente || { ...CLIENTE_POR_DEFECTO };
    if (cliente) {
      localStorage.setItem(CLAVE_CLIENTE, JSON.stringify(cliente));
    } else {
      localStorage.removeItem(CLAVE_CLIENTE);
    }
  },
  limpiarCliente(state) {
    state.cliente = { ...CLIENTE_POR_DEFECTO };
    localStorage.removeItem(CLAVE_CLIENTE);
  },

  // Armar el modo que ya está armado lo apaga: el cajero que se arrepiente aprieta el
  // mismo botón otra vez, en vez de tener que buscar un "cancelar" aparte.
  armarModoEscaneo(state, modo) {
    if (!MODOS_ESCANEO_ARMABLES.includes(modo) || state.modoEscaneo === modo) {
      state.modoEscaneo = MODO_ESCANEO_NORMAL;
      return;
    }
    state.modoEscaneo = modo;
  },

  desarmarModoEscaneo(state) {
    state.modoEscaneo = MODO_ESCANEO_NORMAL;
  },
  //quiero que setee el getter totalCarrito con lo que viene en el payload
  setTotalCarrito(state, total)
  {
    state.totalCarritoManual = total;
  },
  resetTotalCarrito(state)
  {
    state.totalCarritoManual = null;
  },
  setDatosResumen(state, datos) {
    state.datosResumen = datos;
  },
  resetDatosResumen(state) {
    state.datosResumen = null;
  },
  cargarMasivaCarrito(state, payload)
  {
    // Asegúrate de que el payload contenga una lista válida de productos
    if (Array.isArray(payload) && payload.length)
    {
      // Remplaza el contenido del carrito con los nuevos productos del payload
      state.carrito = payload.map(producto =>
      {
        // Asegurarse de que las unidades sean numéricas y válidas
        const unidades = Number(producto.unidades);
        return {
          ...producto,
          unidades: isNaN(unidades) || unidades === 0 ? 1 : unidades
        };
      });

      // Actualizar el localStorage con el nuevo estado del carrito
      localStorage.setItem('carrito', JSON.stringify(state.carrito));

      // Verificar si el total del carrito alcanza el límite
      const total = getters.totalCarrito(state);
      if (total >= 170000)
      {
        // state.activarModuloPago = true;
      }
    } else
    {
      console.warn("El payload para cargar el carrito está vacío o no es válido.");
    }

  },
  activarPago(state, payload)
  {
    state.activarModuloPago = payload;
  },
  deleteIdCarrito(state, payload)
  {
    // Buscar el índice del producto en el carrito
    const index = state.carrito.findIndex(
      (item) => item.id_producto === payload.id
    );

    if (index !== -1)
    {
      // Eliminar el producto del carrito
      state.carrito.splice(index, 1);
    }

    localStorage.setItem('carrito', JSON.stringify(state.carrito));
  },
  addCarrito(state, payload) {
    // Asegurar que unidades tenga un valor válido
    const unidades = Number(payload.unidades);
    const campo = {
      ...payload,
      unidades: isNaN(unidades) || unidades === 0 ? 1 : unidades,
      // Guardar precio original del sistema para referencia si el cajero lo modifica
      precio_unitario_sistema: payload.precio_unitario_sistema || payload.precio_unitario,
    };

    const existingItemIndex = state.carrito.findIndex(
      (item) => item.id_producto === campo.id_producto
    );

    if (existingItemIndex !== -1) {
      const unidadesExistente = Number(state.carrito[existingItemIndex].unidades) || 0;
      const nuevasUnidades = Number(campo.unidades) + unidadesExistente;
      state.carrito[existingItemIndex].unidades = isNaN(nuevasUnidades) ? 1 : nuevasUnidades;
      
      // Si es producto fiambre (pesable) O producto de bulto cerrado con dialog, sumar también la cantidad (peso)
      if ((campo.fiambre === 1 || campo.requiere_dialog_unidades) && campo.cantidad !== undefined && campo.cantidad !== null) {
        const cantidadExistente = Number(state.carrito[existingItemIndex].cantidad || 0);
        state.carrito[existingItemIndex].cantidad = Number(campo.cantidad) + cantidadExistente;
      }
    } else {
      state.carrito.unshift(campo);
    }

    // Verificar el total después de añadir el producto
    const total = getters.totalCarrito(state);
    if (total >= 170000) {
      // state.activarModuloPago = true;
    }

    localStorage.setItem('carrito', JSON.stringify(state.carrito));
  },
  addPago(state, payload) {
    // Asegurarnos de que el pago tenga valores numéricos
    const pago = {
      ...payload,
      pago: Number(payload.pago || 0),
      montoIngresado: Number(payload.montoIngresado || 0)
    };
    state.mediosPagos.push(pago);
  },
  setPorcentajeDescuento(state, porcentaje)
  {
    state.porcentaje = Number(porcentaje);
  },

  addNotaCredito(state, nc) {
    state.notasCreditoAplicadas.push(nc);
  },
  
  removeNotaCredito(state, idNC) {
    const index = state.notasCreditoAplicadas.findIndex(nc => nc.id_nc === idNC);
    if (index !== -1) {
      state.notasCreditoAplicadas.splice(index, 1);
    }
  },
  
  limpiarNotasCredito(state) {
    state.notasCreditoAplicadas = [];
  },
  
  limpiarCarrito(state)
  {
    state.carrito = [];
    state.porcentaje = 0;
    localStorage.setItem('carrito', JSON.stringify(state.carrito));
  },
  finalizarVenta(state, payload)
  {
    state.carrito = [];
    state.activarModuloPago = false;
    state.mediosPagos = [];
    state.notasCreditoAplicadas = []; // Limpiar NC aplicadas
    state.porcentaje = 0; // Resetear el porcentaje al finalizar
    // El cliente se limpia con la venta: la siguiente arranca en Consumidor Final, nunca
    // heredando el cliente de la anterior.
    state.cliente = { ...CLIENTE_POR_DEFECTO };
    // Un modo armado no puede sobrevivir a la venta: el primer escaneo de la venta
    // siguiente sería un borrado que nadie pidió.
    state.modoEscaneo = MODO_ESCANEO_NORMAL;
    localStorage.removeItem('carrito');
    localStorage.removeItem('datosFactura');
    localStorage.removeItem(CLAVE_CLIENTE);
    localStorage.setItem('idUltimoPedido', payload);
  },
  eliminarMediosPagosState(state)
  {
    state.mediosPagos = [];
  },
  setMediosPagos(state, payload) {
    state.mediosPagos = Array.isArray(payload) ? payload : [];
  },
  getProductosLoadPOS(state, payload)
  {
    state.productosPOS = payload;
    // state.loderProducts = false;
  }

};
const actions = {
  //quiero una action para que setee el gettertotalCarrito con lo que viene en el payload
  setTotalCarrito({ commit }, total)
  {
    commit('setTotalCarrito', total);
  },
  resetTotalCarrito({ commit })
  {
    commit('resetTotalCarrito');
  },
  setDatosResumen({ commit }, datos) {
    commit('setDatosResumen', datos);
  },
  resetDatosResumen({ commit }) {
    commit('resetDatosResumen');
  },
  aplicarDescuento({ commit }, porcentaje)
  {
    commit('setPorcentajeDescuento', porcentaje);
  },

  handleCarrito({ commit, getters, state }, producto) {
    return new Promise((resolve, reject) => {
      // Calculamos el total actual usando la lógica correcta para productos pesables
      const carritoActual = state.carrito;
      const totalActual = carritoActual.reduce((total, item) => {
        return total + calcularTotalItem(item);
      }, 0);

      // Calculamos cuánto agregaría el nuevo producto
      const productoTotal = calcularTotalItem(producto);
      
      if (producto.eliminar) {
        commit("deleteIdCarrito", producto);
        resolve();
      } else if (totalActual + productoTotal > 50000000) {
        reject("No se puede agregar este producto. El carrito superaría el límite de 170.000 pesos.");
      } else {
        commit("addCarrito", producto);
        resolve();
      }
    });
  },
  handleCarritoID({ commit }, payload)
  {
    return new Promise(async (resolve, reject) =>
    {
      try
      {
        let r = await axios.get(
          process.env.VUE_APP_BASE_URL + "pedidos/" + payload + "/detalle"
        );
        let detallePedidos= r.data.data.detalle_pedido
        commit("cargarMasivaCarrito", detallePedidos)
        resolve()
      } catch (e)
      {
        console.error(e);
        reject("No se encontro la compra")
      }

    })
  },
  handlePago({ commit }, payload)
  {
    commit("activarPago", payload);
  },
  eliminarMediosPagos({ commit })
  {
    commit("eliminarMediosPagosState");
  },
  setMediosPagos({ commit }, payload) {
    commit('setMediosPagos', payload);
  },
  handleMedioPago({ commit }, payload)
  {
    commit("addPago", payload);
  },
  handleFinalizarVenta({ commit }, payload)
  {
    commit("finalizarVenta", payload);
  },
  reemplazarCarrito({ commit }, payload)
  {
    // Reemplaza todo el carrito con los datos que envíes
    commit("cargarMasivaCarrito", payload);
  },
  getProductosPOS({ commit, state }, refresh)
  {
    return new Promise(async (resolve) =>
    {
      try
      {

        if (state.productosPOS.length !== 0 && !refresh)
        {
          resolve();
          return;
        }
        state.productosPOS = [];
        // state.loderProducts = true;
        let resp = await axios.get(process.env.VUE_APP_BASE_URL + "products-pos");
        commit("getProductosLoadPOS", resp.data);
        resolve();
      } catch (e)
      {
        console.error("erroorrrr", e);
      }
    });
  }
};
export default {
  state,
  getters,
  actions,
  mutations,
};
